import React, {
  FC,
  useEffect,
  useMemo,
} from 'react';

import {
  generatePath, useHistory, useLocation, useParams,
} from 'react-router-dom';
import { AdminRouteName } from '../../CONSTANTS';
import DashboardHeader from '../../../components/DashboardHeader';
import useFilterMethods from '../../../customHooks/useFilterMethods';
import useGetQueryParams from '../../../customHooks/useGetQueryParams';
import useToast from '../../../customHooks/useToast';
import CustomerTabs from '../../AdminCustomers/CustomerTabs';
import AdminTable from '../../../components/AdminTable';
import useTableColumn from '../../../customHooks/useTableColumn';
import EmptyRecord from '../../../components/EmptyRecord';
import EmptyMerchantSettlement from '../../../svgs/EmptyMerchantSettlement';
import useCustomQuery from '../../../network/useCustomQuery';
import { GET_ADMIN_APARTMENTS } from '../../../graphql/admin_queries';
import { cleanUpHomeApplications } from '../../../utils/helpers';
import generatePDF from '../../../utils/generatePdf';
import generateCSV from '../../../utils/generateCSV';

const LIMIT = 14;

const MainHousingApplications: FC = () => {
  const baseDynamicPath = `${AdminRouteName.housingApplicationStatusRoute.toString()}` as const;
  // awaiting_payment, submitted, processing, approved, refunded, completed

  const columns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'city',
    'province',
    'amount',
    'dateApplied',
    'dateCompleted',
    'status',
  ];

  const columnsTitle = {
    name: 'Customer',
    city: 'City',
    province: 'Province',
    amount: 'Amount Paid',
    dateApplied: 'Date Applied',
    dateCompleted: 'Date Completed',
    status: 'Status',
  };

  const { getQuery, getFilterQueries } = useGetQueryParams();
  const { status } = useParams<{status: string}>();
  const [getApartments, loading, rawResponse] = useCustomQuery({
    query: GET_ADMIN_APARTMENTS,
    onError: () => {},
    onData: () => {},
  });

  const page = Number(getQuery('page') || '1');
  const location = useLocation();
  const { push } = useHistory();
  const Toast = useToast();

  const extractData = (response: any) => {
    if (response) {
      const response = rawResponse?.getAdminApartmentDetailPaginated;
      const { data } = response ?? {};
      return data;
    }
  };

  const data = extractData(rawResponse);

  const tabs = [
    {
      name: 'All', link: AdminRouteName.housingApplicationRoute, count: data?.count,
    },
    {
      name: 'Awaiting Payment', link: generatePath(baseDynamicPath, { status: 'awaiting_payment' }), count: data?.count,
    },
    {
      name: 'Pending', link: generatePath(baseDynamicPath, { status: 'submitted' }), count: data?.count,
    },
    {
      name: 'Processing', link: generatePath(baseDynamicPath, { status: 'processing' }), count: data?.count,
    },
    {
      name: 'Approved', link: generatePath(baseDynamicPath, { status: 'approved' }), count: data?.count,
    },
    {
      name: 'Completed', link: generatePath(baseDynamicPath, { status: 'Completed' }), count: data?.count,
    },
    {
      name: 'Refunded', link: generatePath(baseDynamicPath, { status: 'refunded' }), count: data?.count,
    },
  ];

  const {
    getColumns,
    // applyChanges,
    // selectedColumns,
  } = useTableColumn(columns, columnsTitle);
  const search = getQuery('search') || '';

  const onPageLoad = async (offset, limit = LIMIT) => {
    if (page) {
      const result = getFilterQueries();
      getApartments({
        variables: {
          offset,
          limit,
          search,
          statusFilter: status,
          filters: {
            from: result?.from,
            to: result?.to,
            ...(status ? {} : { status: result?.status }),
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });
    }
  };

  useEffect(() => {
    onPageLoad((page - 1) * LIMIT);
  }, [page, search]);

  function onFilterSubmit(result) {
    getApartments({
      variables: {
        offset: 0,
        limit: LIMIT,
        search,
        ...(status ? { statusFilter: status } : {}),
        filters: {
          from: result?.from,
          to: result?.to,
          status: result?.status,
          minAmount: result?.min ? Number(result?.min) : 0,
          maxAmount: result?.max ? Number(result?.max) : 0,
        },
      },
    });
  }

  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: status ? ['dateRange', 'amountRange'] : ['dateRange', 'status', 'amountRange'],
    defaultUrl: location.pathname,
    cb: onFilterSubmit,
    Toast,
    customFilterInformation: {
      dateRange: {
        label: 'Date Applied',
      },
      amountRange: {
        label: 'Amount Paid',
      },
      status: {
        label: 'Status',
        options: [
          {
            label: 'Awaiting Payment',
            value: 'awaiting_payment',
          },
          {
            label: 'Pending',
            value: 'submitted',
          },
          {
            label: 'Processing',
            value: 'processing',
          },
          {
            label: 'Approved',
            value: 'approved',
          },
          {
            label: 'Completed',
            value: 'Completed',
          },
          {
            label: 'Refunded',
            value: 'refunded',
          },
        ],
      },
    },
  });

  const onSearchChange = (e) => {
    push(`${location.pathname}?search=${encodeURIComponent(e.target.value)}`);
  };

  const rawData = useMemo(() => cleanUpHomeApplications(data?.rows), [data]);

  const handleExport = async (type: string) => {
    try {
      const payloadResponse = await onPageLoad(0, 2000);
      const data = extractData(payloadResponse);
      const rec = cleanUpHomeApplications(data?.rows);
      if (data.rows) {
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
        ];

        func({
          payload: rec,
          title: 'CUSTOMER',
          selectedHeaders,
          formatHeaders: getColumns,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  return (
    <>
      <DashboardHeader
        title="Guarantees"
        searchPlaceholder="Customer name / Email address / City"
        search={search}
        onSearchChange={onSearchChange}
        searchLoading={loading}
        filterObj={filterObj}
        exportObj={{
          handleExport,
        }}
      >
        <CustomerTabs Tabs={tabs} />
      </DashboardHeader>
      <div className="merchant-table-details-cover">
        {data?.count ? (
          <AdminTable
            refreshing={loading}
            page={page}
            setPage={setPage}
            pageSize={LIMIT}
            minHeight="calc(100vh - 280px)"
            count={data?.count}
            contents={rawData}
            reference="id"
            // hideDominantImage
            clickable
            hasNameAndImage
            handleClick={
              (data) => push(
                generatePath(AdminRouteName.housingApplicationDetailsRoute, { id: data.id }),
              )
            }
            allowKeyArr={getColumns(true)}
            mobileAllowKeyArr={
              [
                'status',
                ['amount', 'name'],
                ['dateApplied', 'dateCompleted'],
              ]
            }
            formatLabels={columnsTitle}
          />
        ) : (
          <EmptyRecord
            height="calc(100vh - 240px)"
            title="No Data"
            main="All Housing Applications will appear here on the dashboard."
            // footer="Receive Payments"
            loading={loading}
            Icon={EmptyMerchantSettlement}
          />
        )}
      </div>
    </>
  );
};

export default MainHousingApplications;
